"use client";

import { CSSProperties, FC, ReactNode, useState } from "react";
import { VerticalTabMobile } from "./VerticalTabMobile";
import { VerticalTabDesktop } from "./VerticalTabDesktop";

export interface VerticalTabPaging {
  firstTab: number;
  lastTab: number;
  setFirstTab: (tab: number) => void;
  isTabVisible: (tab: number) => boolean;
}

export type VerticalTabProps = {
  variant: "default" | "multiline";
  tabs: {
    label: string;
    head?: string;
    description?: string;
    dlt: string;
    dld: string;
  }[];
  children: ReactNode[];
  theme?: "black" | "gray" | "white";
  tabHeader?: string;
  previousAnalytics?: { dlt: string; dld: string };
  nextAnalytics?: { dlt: string; dld: string };
  nextItem?: {
    displayNextItemLink?: boolean;
    nextItemText: string;
    dlt: string;
    dld: string;
  };
  shownFeatures?: number;
  pagerLabel?: string;
  pagerContainerStyle?: CSSProperties;
  tabClickOverride?: boolean;
};

export const VerticalTab: FC<VerticalTabProps> = ({
  variant = "default",
  theme,
  tabs,
  children,
  tabHeader,
  previousAnalytics,
  nextAnalytics,
  nextItem,
  shownFeatures = 4,
  pagerLabel,
  pagerContainerStyle,
  tabClickOverride = false,
}) => {
  const [firstTab, setFirstTab] = useState<number>(0);

  if (!tabs?.length) return null;

  const tabItemsLength = tabs.length || 0;

  const tabsRemaining = tabItemsLength - 1 - firstTab;
  const limitNumberOfShownFeatures = shownFeatures - 1;
  const lastTab =
    tabsRemaining > limitNumberOfShownFeatures
      ? firstTab + limitNumberOfShownFeatures
      : tabItemsLength - 1;

  const isTabVisible = (tabIndex: number) =>
    tabIndex >= firstTab && tabIndex <= lastTab;

  const tabPaging: VerticalTabPaging = {
    firstTab,
    lastTab,
    setFirstTab,
    isTabVisible,
  };

  return (
    <>
      <div className="hidden lg:flex">
        <VerticalTabDesktop
          variant={variant}
          tabs={tabs}
          theme={theme}
          tabHeader={tabHeader}
          previousAnalytics={previousAnalytics}
          nextAnalytics={nextAnalytics}
          nextItem={nextItem}
          shownFeatures={shownFeatures}
          pagerLabel={pagerLabel}
          pagerContainerStyle={pagerContainerStyle}
          tabPaging={tabPaging}
          tabClickOverride={tabClickOverride}
        >
          {children}
        </VerticalTabDesktop>
      </div>
      <div className="lg:hidden">
        <VerticalTabMobile
          variant={variant}
          tabs={tabs}
          theme={theme}
          tabHeader={tabHeader}
          previousAnalytics={previousAnalytics}
          nextAnalytics={nextAnalytics}
          nextItem={nextItem}
          shownFeatures={shownFeatures}
          pagerLabel={pagerLabel}
          pagerContainerStyle={pagerContainerStyle}
          tabPaging={tabPaging}
        >
          {children}
        </VerticalTabMobile>
      </div>
    </>
  );
};
